import { useReducedMotion } from 'framer-motion'
import type { CommunityRecentMember } from '../../lib/api/community'
import { publicDisplayFirstName } from '../../lib/originCityCoords'
import { useT } from '../../i18n/t'

type RecentMembersMarqueeProps = {
  members: CommunityRecentMember[]
  className?: string
}

type MarqueeItem = {
  id: string
  label: string
}

function buildItems(members: CommunityRecentMember[], anonymous: string): MarqueeItem[] {
  return members
    .map((member) => {
      const firstName = publicDisplayFirstName(member.fullName)
      const origin = member.origin?.trim()
      const label =
        firstName && origin ? `${firstName} · ${origin}`
        : firstName ? firstName
        : origin ? origin
        : anonymous
      return { id: member.userId, label }
    })
    .filter((item) => item.label !== anonymous || members.length > 0)
}

export function RecentMembersMarquee({ members, className = '' }: RecentMembersMarqueeProps) {
  const { t } = useT()
  const reduceMotion = useReducedMotion()
  const anonymous = t('community.post.author.anonymous')
  const items = buildItems(members, anonymous)

  if (!items.length) return null

  if (reduceMotion) {
    return (
      <ul
        aria-label={t('landing.socialProof.recent.aria')}
        className={`flex flex-wrap gap-2 ${className}`}
      >
        {items.map((item) => (
          <li
            key={item.id}
            className="inline-flex pds-tag px-3 py-1.5 text-[13px] font-semibold text-ink/80"
          >
            {item.label}
          </li>
        ))}
      </ul>
    )
  }

  const renderRow = (suffix: string, decorative: boolean) =>
    items.map((item) => (
      <li key={`${item.id}${suffix}`} aria-hidden={decorative ? true : undefined}>
        <span className="inline-flex shrink-0 pds-tag px-3 py-1.5 text-[13px] font-semibold text-ink/80">
          {item.label}
        </span>
      </li>
    ))

  return (
    <div className={`recent-members-marquee relative ${className}`}>
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/55">
        {t('landing.socialProof.recent.eyebrow')}
      </p>
      <div className="overflow-hidden pds-surface bg-ink/[0.02] py-3">
        <ul
          aria-label={t('landing.socialProof.recent.aria')}
          className="recent-members-marquee-track items-center gap-3 px-1"
        >
          {renderRow('', false)}
          {renderRow('-dup', true)}
        </ul>
      </div>
    </div>
  )
}
